/**
 * Adresskopf aus Word einfügen.
 *
 * Wer einen Bestand einpflegt, hat die Anschrift meist schon irgendwo: im
 * Briefkopf einer alten Rechnung, in einer Mail, in einer Word-Vorlage. Sie
 * in sieben Felder abzutippen ist Arbeit ohne Erkenntnis – und ein
 * Tippfehler in der Anschrift fällt erst auf, wenn eine Rechnung
 * zurückkommt.
 *
 * Zwei Regeln, an denen sich das Verhalten ausrichtet:
 *
 * **Es füllt nur leere Felder.** Was jemand schon eingetragen hat, wiegt
 * schwerer als was ein Verfahren aus einem Briefkopf liest.
 *
 * **Nichts verschwindet.** Zeilen ohne erkennbare Rolle stehen darunter im
 * Klartext. Eine Erkennung, die eine Zeile stillschweigend fallen lässt,
 * ist schlimmer als eine, die weniger erkennt – man merkt es nicht.
 */

import { useState } from 'react'
import { type Adresserkennung, useAdresseErkennen } from '../api/adresse'
import { Fehlerhinweis } from './Hinweise'

/** Welches erkannte Feld in welches Formularfeld gehört. */
const ZUORDNUNG: [keyof Adresserkennung, string][] = [
  ['name', 'name'],
  ['strasse', 'strasse'],
  ['plz', 'plz'],
  ['ort', 'ort'],
  ['ansprechpartner', 'ansprechpartner'],
  ['telefon', 'telefon'],
  ['email', 'email'],
]

const BESCHRIFTUNG: Record<string, string> = {
  name: 'Name',
  strasse: 'Straße',
  plz: 'PLZ',
  ort: 'Ort',
  ansprechpartner: 'Ansprechpartner',
  telefon: 'Telefon',
  email: 'E-Mail',
}

export function Adressuebernahme({ formular }: { formular: HTMLFormElement | null }) {
  const erkennen = useAdresseErkennen()
  const [text, setzeText] = useState('')
  const [uebernommen, setzeUebernommen] = useState<string[]>([])
  const [uebergangen, setzeUebergangen] = useState<string[]>([])
  const [rest, setzeRest] = useState<string[]>([])

  function eintragen(erkannt: Adresserkennung) {
    const gefuellt: string[] = []
    const schon: string[] = []

    for (const [quelle, ziel] of ZUORDNUNG) {
      const wert = erkannt[quelle]
      if (typeof wert !== 'string' || !wert) continue
      const feld = formular?.elements.namedItem(ziel)
      if (!(feld instanceof HTMLInputElement)) continue
      if (feld.value.trim()) {
        // Nicht überschreiben: Getipptes wiegt schwerer als Erkanntes.
        schon.push(BESCHRIFTUNG[ziel] ?? ziel)
        continue
      }
      feld.value = wert
      gefuellt.push(BESCHRIFTUNG[ziel] ?? ziel)
    }

    setzeUebernommen(gefuellt)
    setzeUebergangen(schon)
    // Die Abteilung hat im Kundenformular kein Feld – sie gehört zum
    // Ansprechpartner und wird dort gepflegt. Sie geht deshalb in den Rest,
    // statt zu verschwinden.
    setzeRest([...(erkannt.funktion ? [erkannt.funktion] : []), ...erkannt.rest])
  }

  return (
    <div className="rounded-feld border-2 border-dashed border-slate-300 p-3">
      <label htmlFor="adresskopf">Adresse aus Word einfügen</label>
      <p className="mb-1 text-base text-slate-600">
        Den ganzen Briefkopf hier hineinkopieren — Name, Straße, PLZ und Ort,
        Telefon, E-Mail. Die Felder darunter werden ausgefüllt und lassen sich
        danach ändern.
      </p>
      <textarea
        id="adresskopf"
        rows={4}
        value={text}
        placeholder={'Gemeinde Krummhörn\nBauamt\nRathausstr. 1\n26736 Pewsum'}
        onChange={(e) => setzeText(e.target.value)}
        onPaste={(e) => {
          // Gleich beim Einfügen erkennen. Ein zusätzlicher Knopf wäre ein
          // Handgriff mehr für etwas, das ohnehin gewollt ist.
          const eingefuegt = e.clipboardData.getData('text')
          if (eingefuegt.trim()) {
            erkennen.mutate(eingefuegt, { onSuccess: eintragen })
          }
        }}
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="knopf-still px-3 text-base"
          disabled={!text.trim() || erkennen.isPending}
          onClick={() => erkennen.mutate(text, { onSuccess: eintragen })}
        >
          {erkennen.isPending ? 'Liest …' : 'Erneut erkennen'}
        </button>
        {uebernommen.length > 0 && (
          <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
            Übernommen: {uebernommen.join(', ')}
          </span>
        )}
      </div>

      {uebergangen.length > 0 && (
        <p className="mt-2 text-base text-slate-700">
          Nicht überschrieben, weil dort schon etwas stand:{' '}
          {uebergangen.join(', ')}.
        </p>
      )}

      {rest.length > 0 && (
        <div className="mt-2 text-base text-slate-700">
          <p className="font-semibold">Nicht zugeordnet:</p>
          <ul className="list-inside list-disc">
            {rest.map((z, i) => (
              <li key={i}>{z}</li>
            ))}
          </ul>
        </div>
      )}

      <Fehlerhinweis fehler={erkennen.error} />
    </div>
  )
}
